import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Checkbox,
  Space,
  App,
  Grid,
} from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export default function LoginDashboard() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const handleLogin = async (values) => {
    setLoading(true);

    try {
      const response = await axios.post(
        "/api/user/login",
        {
          username: values.username,
          password: values.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Login response:", response.data);

      // Store API key if returned
      const apiKeyFromResponse =
        response.data?.data?.apiKey ||
        response.data?.data?.api_key ||
        response.data?.apiKey ||
        response.data?.api_key;

      if (apiKeyFromResponse) {
        localStorage.setItem("apiKey", apiKeyFromResponse);
        console.log("API key stored in localStorage");
      }

      // Store user role
      const userRole = response.data?.data?.user_role || "client";
      localStorage.setItem("user_role", userRole);
      console.log("User role stored:", userRole);

      message.success("Login successful!", 3);
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("api_credits", "50");
      localStorage.setItem("api_credits_last_refill", Date.now().toString());
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "Login failed. Please try again.";

      if (error.response) {
        const responseData = error.response.data;

        if (error.response.status === 401) {
          if (
            responseData &&
            typeof responseData === "object" &&
            responseData.error
          ) {
            errorMessage = String(responseData.error);
          } else if (typeof responseData === "string") {
            errorMessage = responseData;
          } else {
            errorMessage =
              "Invalid username or password. Please check your credentials and try again.";
          }
        } else if (error.response.status === 400) {
          if (
            responseData &&
            typeof responseData === "object" &&
            responseData.error
          ) {
            errorMessage = String(responseData.error);
          } else if (typeof responseData === "string") {
            errorMessage = responseData;
          } else {
            errorMessage = "Username and password are required.";
          }
        } else {
          if (responseData && typeof responseData === "object") {
            errorMessage = String(
              responseData.error ||
                responseData.message ||
                responseData.errorMessage ||
                `Login failed: ${error.response.status} ${
                  error.response.statusText || "Error"
                }`
            );
          } else if (typeof responseData === "string") {
            errorMessage = responseData;
          } else {
            errorMessage = `Login failed: ${error.response.status} ${
              error.response.statusText || "Error"
            }`;
          }
        }
      } else if (error.request) {
        errorMessage =
          "No response from server. Please check if the backend is running.";
      } else {
        errorMessage = error.message || "Login failed. Please try again.";
      }

      // Ensure errorMessage is always a string
      if (typeof errorMessage !== "string") {
        errorMessage = String(errorMessage);
      }

      if (
        !errorMessage ||
        errorMessage === "true" ||
        errorMessage === "false"
      ) {
        errorMessage =
          "Invalid username or password. Please check your credentials and try again.";
      }

      message.error(errorMessage, 4);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)",
        padding: isMobile ? "16px" : "24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Futuristic background effects */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(0, 217, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0, 102, 255, 0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          gap: isMobile ? "32px" : "64px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: isMobile ? "100%" : "450px",
            color: "white",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Title
            level={1}
            style={{
              color: "white",
              marginBottom: isMobile ? "12px" : "24px",
              fontSize: isMobile ? "32px" : "48px",
              fontWeight: 700,
              lineHeight: 1.2,
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Welcome to{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #00d9ff 0%, #ffffff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              VerifyU
            </span>
          </Title>
          <Text
            style={{
              color: "rgba(255, 255, 255, 0.95)",
              fontSize: isMobile ? "15px" : "18px",
              lineHeight: 1.7,
              textShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            VerifyU is your trusted partner for seamless, secure, and efficient
            KYC solutions. We make KYC simple, secure, and customer-friendly.
          </Text>
        </div>

        <Card
          style={{
            width: "100%",
            maxWidth: "450px",
            borderRadius: 16,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)",
          }}
          styles={{ body: { padding: isMobile ? "24px" : "40px" } }}
        >
          <Title
            level={2}
            style={{
              textAlign: "center",
              marginBottom: "32px",
              background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
            }}
          >
            Admin Login
          </Title>

          <Form
            form={form}
            name="login"
            onFinish={handleLogin}
            layout="vertical"
            size="large"
            autoComplete="off"
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: "Please input your username!" },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: "#0066ff" }} />}
                placeholder="Username"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please input your password!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#0066ff" }} />}
                placeholder="Password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
                <a
                  href="#"
                  style={{
                    fontSize: "14px",
                    color: "#0066ff",
                    fontWeight: 500,
                  }}
                >
                  Forgot password?
                </a>
              </Space>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  height: "44px",
                  background:
                    "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0, 102, 255, 0.3)",
                }}
              >
                Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
}
